import { db, auth } from "../firebaseConfig";
import { collection, getDocs, doc, getDoc, addDoc, setDoc, updateDoc, query, where, arrayUnion } from "firebase/firestore";

export default {
  name: "ProjectListLogic",
  data() {
    return {
      projects: [],
      loading: true,
      error: "",
      newProjectName: "",
      showAddProjectModal: false,
      projectFilter: "active",
      searchTerm: "",                  // ← comma added
      assignableUsers: [],             
      selectedDesigner: null,          // for “Assign Production/Admin Members”
      selectedDesigners: [],           // array of IDs added
      selectedLeadingProducer: null    // single‐select Leading Producer
    };
  },
  computed: {
    filteredProjects() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.projects;
  
      const words = term.split(" ").filter(Boolean);
  
      return this.projects.filter(project => {
        const combined = [
          ...(project.keywords || []),
          ...(project.searchString ? [project.searchString] : [])
        ].join(" ");
        return words.every(word => combined.includes(word));
      });
    },
    // only those with role === "production"
    onlyProducers() {
      return this.assignableUsers.filter(u => u.role === "producer");
    },
    // now reactive!
    isFormValid() {
      return (
        this.newProjectName.trim().length > 0 &&
        this.selectedDesigners.length > 0 &&
        !!this.selectedLeadingProducer
      );
    }
  },
  async created() {
    await this.fetchProjects();
    await this.fetchAssignableUsers();
  },
  methods: {
    async fetchProjects() {
      try {
        const projectsRef = collection(db, "projects");
        const projectsSnap = await getDocs(projectsRef);
    
        const projectPromises = projectsSnap.docs.map(async (projectDoc) => {
          const projectId = projectDoc.id;
          // Get main data from the project document (where totalFeedbackScore is stored)
          const mainData = projectDoc.data();
          const detailsRef = doc(db, "projects", projectId, "projectDetails", "details");
          const detailsSnap = await getDoc(detailsRef);
    
          if (!detailsSnap.exists()) {
            console.error(`Project details not found for project ${projectId}`);
            return null;
          }
    
          // Get the details data and merge with mainData
          let detailsData = detailsSnap.data();
          // Merge the totalFeedbackScore from the main document (or default to 100)
          detailsData.totalFeedbackScore = mainData.totalFeedbackScore || 100;
          
          // Process producers if available
          if (!Array.isArray(detailsData.producers)) {
            detailsData.producers = detailsData.producers ? [detailsData.producers] : [];
          }
    
          if (detailsData.producers.length > 0) {
            const producerNames = await Promise.all(
              detailsData.producers.map(async (producerId) => {
                const userRef = doc(db, "users", producerId);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                  const userData = userSnap.data();
                  return `${userData.firstName} ${userData.surname}`;
                } else {
                  return producerId;
                }
              })
            );
            detailsData.producers = producerNames;
          }       
          
          // Update submittedBy with full name if possible
          if (detailsData.submittedBy) {
            const userRef = doc(db, "users", detailsData.submittedBy);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              detailsData.submittedBy = userData.fullName;
            }
          }
          return { id: projectId, ...detailsData };
        });
    
        const projectList = await Promise.all(projectPromises);
        // Filter projects based on projectFilter (default is 'active')
        this.projects = projectList.filter(
          (p) => p !== null && p.projectState === this.projectFilter
        );
      } catch (error) {
        console.error("Error fetching projects:", error);
        this.error = "Error fetching projects.";
      } finally {
        this.loading = false;
      }
    },    
    openAddProjectModal() {
      this.newProjectName = "";
      this.showAddProjectModal = true;
    },
    closeAddProjectModal() {
      this.showAddProjectModal = false;
    },
    // Generate keywords and search string from project name
    generateKeywords(projectName) {
      const cleaned = projectName.replace(/[_-]+/g, " ");
      const words = cleaned.split(" ").map(w => w.trim()).filter(Boolean);
      const uniqueWords = Array.from(new Set(words));
      const keywords = uniqueWords.map(word => word.toLowerCase());
      const searchString = keywords.join(" ");
      return { keywords, searchString };
    },
        // new: load all users whose role === "production"
        async fetchAssignableUsers() {
          const usersRef = collection(db, "users");
          const q = query(usersRef, where("role", "in", ["production", "admin", "producer"]));
          const snap = await getDocs(q);
          this.assignableUsers = snap.docs.map(d => {
            const { firstName, surname, fullName, role } = d.data();
            return {
              id: d.id,
              fullName: fullName || `${firstName} ${surname}`,
              role
            };
          });
        },
    
        addDesigner() {
          if (
            this.selectedDesigner &&
            !this.selectedDesigners.includes(this.selectedDesigner)
          ) {
            this.selectedDesigners.push(this.selectedDesigner);
          }
          this.selectedDesigner = null;
        },        
        removeDesigner(index) {
          this.selectedDesigners.splice(index, 1);
        },
    // 3️⃣ creating a project: include both arrays & string
    async createProject() {
      if (!this.isFormValid) {
        alert("Please fill out all required fields.");
        return;
      }
      
      if (!this.newProjectName.trim()) {
        return alert("Project Name cannot be empty.");
      }
      try {
        const projectRef = await addDoc(collection(db, "projects"), {
          createdAt: new Date()
        });
        const projectId = projectRef.id;

        const { keywords, searchString } = this.generateKeywords(this.newProjectName);
        // write the details doc
        await setDoc(
          doc(db, "projects", projectId, "projectDetails", "details"),
          {
            projectName: this.newProjectName,
            submittedBy: auth.currentUser?.uid || "anonymous",
            createdAt: new Date(),
            projectState: "active",
            keywords,
            searchString,

            // 📌 NEW FIELDS:
            assignedDesigners: this.selectedDesigners,           // array of IDs
            producers: this.selectedLeadingProducer || null      // single ID or null
          }
        );
        // 4️⃣ push the projectId into each user’s profile
        await Promise.all(
          this.selectedDesigners.map(uid =>
            updateDoc(doc(db, "users", uid), {
              projectAssigned: arrayUnion(projectId)
            })
          )
        );

        this.closeAddProjectModal();
        this.$router.push(`/feedback/${projectId}/dashboard`);
      } catch (err) {
        console.error("Error creating project:", err);
        alert("Error creating project.");
      }
    },
    // Backfill keywords and search string for existing projects
    async backfillSearchData() {
      try {
        const projectsSnap = await getDocs(collection(db, "projects"));
        let updatedCount = 0;
    
        for (const projectDoc of projectsSnap.docs) {
          const projectId = projectDoc.id;
          const detailsRef = doc(db, "projects", projectId, "projectDetails", "details");
          const detailsSnap = await getDoc(detailsRef);
    
          if (!detailsSnap.exists()) {
            console.warn(`No details for ${projectId}`);
            continue;
          }
    
          const data = detailsSnap.data();
    
          if (!data.projectName) continue;
          if (data.keywords && data.searchString) continue;
    
          const { keywords, searchString } = this.generateKeywords(data.projectName);
    
          await updateDoc(detailsRef, { keywords, searchString });
          updatedCount++;
        }
    
        alert(`✅ Updated ${updatedCount} project(s) with keywords/searchString.`);
      } catch (err) {
        console.error("Error during backfill:", err);
        alert("❌ Error during backfill. See console.");
      }
    },    
    // Toggle project filter and re-fetch projects
    toggleProjectFilter() {
      this.projectFilter = this.projectFilter === "active" ? "retired" : "active";
      this.fetchProjects();
    }
  }
};